import {
  ACTIVE_PROJECT_STATUSES,
  MINE_PARAM,
  PROJECT_POST_KEYS,
  PROJECT_POSTS,
  type ProjectPost,
} from '@brickup/shared/constants'
import { ProjectStatus } from '@brickup/shared/enums'
import {
  boolFilter,
  FILTER_ALL,
  oneOf,
  stringFilter,
  type AllOr,
  type FilterDef,
} from '@/components/Internal/DataTable'

/**
 * Default status filter: the states staff are actively working on. Public
 * listings, finished and archived projects are excluded until a wider filter
 * is chosen.
 */
export const STATUS_FILTER_ACTIVE = 'active'

export type StatusFilter = AllOr<typeof STATUS_FILTER_ACTIVE | ProjectStatus>

export const projectListFilters = {
  status: {
    // The sentinels have to be in the allowed set too, otherwise a URL that
    // spells one out explicitly fails validation.
    ...oneOf<StatusFilter>(
      [FILTER_ALL, STATUS_FILTER_ACTIVE, ...Object.values(ProjectStatus)],
      STATUS_FILTER_ACTIVE,
    ),
    clearedValue: FILTER_ALL,
    // The active-sentinel names a set the API has no word for, so it expands
    // into the concrete list.
    toQuery: value =>
      value === STATUS_FILTER_ACTIVE
        ? ACTIVE_PROJECT_STATUSES.join(',')
        : value,
  } satisfies FilterDef<StatusFilter>,

  // One filter per post, named by `PROJECT_POSTS`, so a post added to that
  // table is a filter here without a line being written. Any id goes through
  // unchecked: one the API cannot act on comes back as an empty list, which
  // is the answer the reader asked for.
  ...(Object.fromEntries(
    PROJECT_POST_KEYS.map(post => [
      PROJECT_POSTS[post].param,
      stringFilter<string>(FILTER_ALL),
    ]),
  ) as {
    [P in ProjectPost as (typeof PROJECT_POSTS)[P]['param']]: FilterDef<string>
  }),

  // The reader's own projects, which is every post and the access given
  // beside them — the one question no single post filter answers. It carries
  // no id: the server reads it against whoever is asking.
  [MINE_PARAM]: boolFilter(),

  customer: stringFilter<string>(FILTER_ALL),

  search: stringFilter(),
}
